/**
 * Respuestas sugeridas, del lado del candidato.
 *
 * Nace de soporte: la gente le mandaba a Freddy pantallazos de lo que le
 * contestó la empresa preguntando "¿qué digo?". Ahora, cuando la última palabra
 * del hilo es de la empresa, la app explica qué le están diciendo y ofrece
 * borradores que se ponen en la caja de respuesta con un clic.
 *
 * LA REGLA QUE NO SE NEGOCIA: la sugerencia NUNCA se envía sola. "Usar esta
 * respuesta" sólo llena el compositor; mandar sigue siendo el botón de siempre,
 * después de que la persona leyó (y si quiere, cambió) el texto.
 */
import { getLocale, messages, type Locale } from '~/i18n';
import { ThreadApiError, type ThreadMessage } from '../api/threadsClient';

/**
 * El mensaje de la empresa que hay que contestar, o null si no toca sugerir.
 *
 * Toca sólo cuando se puede responder desde acá (`can_reply`, en domain o en un
 * hilo de Gmail que se pudo leer) y el ÚLTIMO mensaje del hilo es entrante. El mismo criterio que el servidor
 * (409 si no), mirado antes para no provocar el error: en un hilo donde la
 * persona ya contestó, la tarjeta simplemente no aparece.
 */
export function messageToSuggestFor(
  thread: { can_reply: boolean },
  threadMessages: ThreadMessage[],
): ThreadMessage | null {
  if (!thread.can_reply) return null;
  const last = threadMessages[threadMessages.length - 1];
  if (!last || last.direction !== 'inbound' || last.synthesized) return null;
  return last;
}

/**
 * Los 409 del servidor (`no_employer_message`, `last_message_from_candidate`)
 * no son errores para la persona: significan "no hay nada que sugerir", y la
 * tarjeta se esconde en silencio en vez de pintar una alerta.
 */
export function isNothingToSuggest(error: unknown): boolean {
  return error instanceof ThreadApiError && error.status === 409;
}

/** La frase para cada falla de la sugerencia. Nunca bloquea: siempre se puede escribir a mano. */
export function suggestionErrorMessage(error: unknown, locale: Locale = getLocale()): string {
  const t = messages(locale).workspace.suggestion.errors;
  if (!(error instanceof ThreadApiError)) return t.network;
  if (error.code === 'rate_limited') {
    const minutes = Math.max(1, Math.ceil((error.retryAfterSeconds ?? 3600) / 60));
    return t.rateLimited(minutes);
  }
  return t.failed;
}
